import CssBaseline from "@mui/material/CssBaseline";
import React, { JSX, useState } from "react";
import { Check2FARequired } from "./steps/Check2FARequired";
import { CheckBlocked } from "./steps/CheckBlocked";
import { CheckPasswordResetRequired } from "./steps/CheckPasswordResetRequired";
import { Configure2FA } from "./steps/Configure2FA";
import { CredentialsForm } from "./steps/CredentialsForm";
import { Enter2FA } from "./steps/Enter2FA";
import { Evaluate2FARequirement } from "./steps/Evaluate2FARequirement";
import { LoginComplete } from "./steps/LoginComplete";
import { LoginContextData, LoginStep } from "./Declarations";
import { ResetPassword } from "./steps/ResetPassword";
import { ThemeProvider } from "@mui/material/styles";
import { createThemeFromSeed } from "@ntg/component-library/theming";
import { FinalizeLogin } from "./steps/FinalizeLogin";
import Box from "@mui/material/Box";

export const LoginStateMachine = () => {
    const [step, setStep] = useState<LoginStep>(LoginStep.CheckBlocked);
    const [loginContext, setLoginContext] = useState<LoginContextData>({
        BrowserFingerprint: undefined,
        LoginMessage: undefined,
        NewPassword: undefined,
        Password: undefined,
        TwoFACode: undefined,
        UserID: undefined,
        UserName: undefined,
        ValidUserNamePassword: undefined,
    });

  const isDarkMode = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
    const theme = createThemeFromSeed({ menuBar: "#3949AB", isDarkMode });

    /**
     * Move to the specified step and optionally update the login context.
     */
    const goTo = (
        nextStep: LoginStep,
        contextUpdates?: Partial<LoginContextData>
    ) => {
        if (contextUpdates) {
            setLoginContext((currentContext) => {
                return {
                    ...currentContext,
                    ...contextUpdates,
                };
            });
        }

        setStep(nextStep);
    };

    /**
     * Define what each step renders.
     */
    const steps: Record<LoginStep, JSX.Element> = {
        [LoginStep.CheckBlocked]: (
            <CheckBlocked
                onAllowed={(context) =>
                    goTo(LoginStep.Credentials, {
                        BrowserFingerprint: context.BrowserFingerprint,
                    })}
            />
        ),

        [LoginStep.Credentials]: (() => {
            const fingerprint = loginContext.BrowserFingerprint;

            const ready = fingerprint !== undefined;

            if (!ready) {
                return <p>Preparing login form...</p>;
            }

            return (
                <>
                    <CredentialsForm
                        BrowserFingerprint={fingerprint}

                        onSuccess={(ctx) => {
                            if (ctx.ValidUserNamePassword === true) {
                                goTo(LoginStep.Evaluate2FARequirement, ctx);
                            } else {
                                // Save the context so we can display LoginMessage in this step
                                setLoginContext((prev) => ({
                                    ...prev,
                                    ...ctx,
                                }));
                            }
                        }}
                    />
                    {loginContext.ValidUserNamePassword === false && loginContext.LoginMessage !== undefined && (
                        <p className="error">{loginContext.LoginMessage}</p>
                    )}

                </>
            );
        })(),

        [LoginStep.Evaluate2FARequirement]: (
            <Evaluate2FARequirement
                data={loginContext}
                onDecision={(configured) =>
                    goTo(
                        configured
                            ? LoginStep.Check2FARequired
                            : LoginStep.Configure2FA
                    )}
            />
        ),

        [LoginStep.Configure2FA]: (
            <Configure2FA
                data={loginContext}
                onComplete={() => {
                    goTo(LoginStep.Enter2FA);
                }}
            />
        ),

        [LoginStep.Check2FARequired]: (
            <Check2FARequired
                data={loginContext}
                onDecision={(isRequired) => {
                    goTo(
                        isRequired
                            ? LoginStep.Enter2FA
                            : LoginStep.CheckPasswordResetRequired
                    );
                }}
            />
        ),

        [LoginStep.Enter2FA]: (
            <Enter2FA
                data={loginContext}
                onVerified={() => {
                    goTo(LoginStep.CheckPasswordResetRequired);
                }}
            />
        ),

        [LoginStep.ResetPassword]: (
            <ResetPassword
                data={loginContext}
                onReset={() => {
                    goTo(LoginStep.FinalizeLogin);
                }}
            />
        ),

        [LoginStep.CheckPasswordResetRequired]: (
            <CheckPasswordResetRequired
                data={loginContext}
                onDecision={(requiresReset) => {
                    goTo(
                        requiresReset
                            ? LoginStep.ResetPassword
                            : LoginStep.FinalizeLogin
                    );
                }}
            />
        ),

        [LoginStep.FinalizeLogin]: (
            <FinalizeLogin
                data={loginContext}
                onComplete={() => goTo(LoginStep.Complete)}
            />
        ),

        [LoginStep.Complete]: <LoginComplete />,
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box p={2}>
                <div className="login-popup">{steps[step]}</div>
            </Box>
        </ThemeProvider>
    );
};
